import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowsLeftRight,
  faArrowsRotate,
  faCircleCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom-latest';
import Select from 'react-select';
import moment from 'moment';
import './styles.scss';
import { debounce } from 'lodash';
import Pagination from 'common/Pagination';
import { useWebsocket } from 'websocket';
import { AlertMessage, getCpaUserId } from 'utilities/utils';
import { selectStyles } from 'common/CustomFields';
import {
  getApiV5FirmsServiceAliases,
  postApiV5FirmsServiceAliasesIdConfirm,
  postApiV5FirmsServiceAliasesIdReset,
} from 'api/v5/service-aliases/service-aliases';
import { postApiV5FirmsAppConnectionsSmartVaultClientsSynchronization } from 'api/v5/smartvault-sync/smartvault-sync';
import ConfirmationModal from 'common/ConfirmationModal';
import {
  PaginationData,
  ServiceAlias,
  ServiceAliases,
  defaultPagination,
  defaultServiceAlias,
  AccountSearchOption,
  SyncStatus,
  ConnectionStatus,
} from './types';
import EditLinkingModal from './EditLinkingModal';

// Per-row sync-health chip (LISD-2519). One uniform pill shape; only the tint +
// text/dot color vary by status, so the column reads consistently. label is the
// firm-facing wording.
const SYNC_STATUS_META: Record<
  SyncStatus,
  { label: string; fg: string; bg: string }
> = {
  synced: { label: 'Synced', fg: '#2b8a3e', bg: '#ebfbee' },
  partial: { label: 'Partial', fg: '#e67700', bg: '#fff9db' },
  needs_reconnect: { label: 'Needs reconnect', fg: '#c92a2a', bg: '#fff5f5' },
  no_readable_vault: {
    label: 'No readable vault',
    fg: '#d9480f',
    bg: '#fff4e6',
  },
  never_synced: { label: 'Never synced', fg: '#495057', bg: '#f1f3f5' },
  no_owner_set: { label: 'No owner set', fg: '#495057', bg: '#f1f3f5' },
  sync_error: { label: 'Sync error', fg: '#c92a2a', bg: '#fff5f5' },
};

// Next-step guidance on the actionable sync-health chips (LISD-2574): the chip is
// where the firm first sees a problem, so the recovery path should be readable on
// hover rather than only after opening the Reset modal. Healthy states get none.
const SYNC_STATUS_TOOLTIP: Partial<Record<SyncStatus, string>> = {
  needs_reconnect:
    'The SmartVault owner is disconnected or cannot authenticate. Reset, then run Sync Accounts as the new owner and Confirm.',
  no_owner_set: 'Run Sync Accounts to assign an owner, then Confirm.',
  sync_error:
    'The last sync failed. Retry Sync Accounts; contact support if it persists.',
};

const SYNC_CHIP_BASE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '2px 10px',
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.6,
  whiteSpace: 'nowrap',
};

// Live primary_owner connection health (LISD-2590), shown beneath the owner email.
// Only the PROBLEM states get a chip — the whole point is to flag an owner whose
// connection is dead while sync_status still reads "Synced" (the lag this fixes).
// `connected` is the silent default (no chip = no noise); `no_owner` shows nothing
// because the Owner cell already renders "-". The chip text is the firm-facing
// wording; the tooltip carries the recovery path (Reset -> Sync -> Confirm).
const OWNER_CONNECTION_META: Partial<
  Record<ConnectionStatus, { label: string; fg: string; bg: string; tip: string }>
> = {
  disconnected: {
    label: 'Disconnected',
    fg: '#c92a2a',
    bg: '#fff5f5',
    tip: "The owner's SmartVault connection needs reconnecting. Reset, then run Sync Accounts as a current owner and Confirm.",
  },
  inactive_employee: {
    label: 'Owner offboarded',
    fg: '#c92a2a',
    bg: '#fff5f5',
    tip: 'The owner employee is deactivated. Reset, then run Sync Accounts as a current employee and Confirm.',
  },
};

// Axios errors don't reliably carry a useful `.message` — HTTP error detail
// lives in `response.data` — so fall through to a caller-supplied fallback
// rather than surfacing "undefined" in a toast.
type MaybeAxiosError = {
  response?: {
    // V5 errors come back as error_object_array ({ errors: [{ message }] });
    // the Pundit 403 uses a top-level { message }. Handle both.
    data?: { message?: string; errors?: Array<{ message?: string }> };
  };
  message?: string;
};
const errorMessageFrom = (error: unknown, fallback: string): string => {
  const e = error as MaybeAxiosError;
  return (
    e?.response?.data?.errors?.[0]?.message ||
    e?.response?.data?.message ||
    e?.message ||
    fallback
  );
};

// A mapping is "complete" once it's owned + linked + confirmed. Such rows are
// edit-locked server-side (re-linking a live mapping would split imported docs
// across accounts), so the row offers Reset instead of Edit — re-establishment
// goes Reset → Sync Accounts → Confirm (LISD-2586).
const isComplete = (sa: ServiceAlias): boolean =>
  Boolean(sa.primary_owner && sa.aliasable_id && sa.confirmed);

export default function SmartVaultConfig() {
  const SYNC_MESSAGE =
    'Syncing accounts from SmartVault. This page will refresh when complete.';
  const navigate = useNavigate();
  const [serviceAliasData, setServiceAliasData] = useState<ServiceAliases>([]);
  const [paginationData, setPaginationData] =
    useState<PaginationData>(defaultPagination);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showOrphans, setShowOrphans] = useState(false);
  const [serviceAliasSearchResults, setServiceAliasSearchResults] = useState<
    AccountSearchOption[]
  >([]);
  const [serviceAliasesQuery, setServiceAliasesQuery] = useState<string | null>(
    null,
  );
  const [selectedServiceAlias, setSelectedServiceAlias] =
    useState<AccountSearchOption | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editableServiceAlias, setEditableServiceAlias] =
    useState<ServiceAlias>(defaultServiceAlias);
  // Alias id whose Confirm POST is in flight — disables its button to prevent
  // double-submits.
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  // Reset (LISD-2586): resetTarget drives the confirmation modal; resettingId
  // disables the modal action while the POST is in flight.
  const [resetTarget, setResetTarget] = useState<ServiceAlias | null>(null);
  const [resettingId, setResettingId] = useState<number | null>(null);
  // Confirm/bulk_confirm are gated behind enableSmartVaultMirror server-side
  // (ServiceAliasesController), so only surface the Syncable column for
  // Mirror firms — otherwise the Confirm action would 404.
  const { enableSmartVaultMirror } = useFlags();
  const websocket = useWebsocket();

  // Keep the completion handler pointed at the CURRENT fetch (active page +
  // filters) rather than the closure captured when the subscription is created
  // at mount — LISD-2574: completion used to refetch page-1 unfiltered and render
  // it under the current controls.
  const fetchRef = useRef<() => void>(() => {});
  // True only while a sync THIS page initiated is awaiting its completion
  // broadcast. Gates the terminal toast so another tab's sync — or a retried
  // backend broadcast — refreshes the table silently instead of re-toasting.
  const awaitingSyncRef = useRef(false);

  const activateActionCable = () => {
    const subscription = websocket.consumer.subscriptions.create(
      { channel: 'SmartVaultAccountSyncChannel', cpa_user_id: getCpaUserId() },
      {
        // The channel broadcasts a bare status string ('complete' | 'failed').
        received: (data: string) => {
          fetchRef.current();
          setIsSyncing(false);
          // Only the page that initiated the sync toasts its result, and only
          // once — dedupes repeated/retried terminal broadcasts and ignores
          // other tabs' syncs (which still refresh the table above).
          if (!awaitingSyncRef.current) return;
          awaitingSyncRef.current = false;
          if (data === 'complete') {
            AlertMessage('success', 'Synchronization complete!', 3000);
          } else if (data === 'failed') {
            AlertMessage(
              'error',
              'SmartVault sync failed — check the connection in Third Party Apps and try again.',
              4000,
            );
          } else {
            // The channel only broadcasts 'complete' | 'failed' (DiscoverAccountsJob),
            // so anything else is a contract drift. Don't let it masquerade as success
            // — this whole flow exists to stop false "complete" toasts — log and stay
            // silent (the table was already refreshed above via fetchRef).
            console.warn(`SmartVaultAccountSyncChannel: unexpected status "${data}"`);
          }
        },
      },
    );

    return subscription;
  };

  const queryParams = () => {
    const defaultQueryParams = {
      aliasable_type: 'Account',
      service_id: 'smartvault',
      external_id: selectedServiceAlias?.value,
      filter_orphaned: showOrphans,
      sort: 'external_name',
    };

    return { ...defaultQueryParams, page };
  };

  const fetchServiceAliases = () => {
    setIsLoading(true);
    getApiV5FirmsServiceAliases(queryParams())
      .then((res) => {
        setServiceAliasData(res.data);
        // The generated Pagination type marks every field optional; the backend
        // always populates them, so coalesce to keep the required PaginationData.
        setPaginationData({
          current_page: res.meta.current_page ?? 0,
          total_pages: res.meta.total_pages ?? 0,
          total_entries: res.meta.total_entries ?? 0,
          per_page: res.meta.per_page ?? 0,
        });
        setIsLoading(false);
      })
      .catch((error) => {
        AlertMessage(
          'error',
          errorMessageFrom(error, 'Could not load linked accounts.'),
          3000,
        );
        setIsLoading(false);
      });
  };

  const fetchServiceAliasSearch = () => {
    // Search the full SmartVault account list — do NOT spread queryParams(), whose
    // external_id (the selected row) would pin results to one account and break
    // searching/switching from the dropdown.
    getApiV5FirmsServiceAliases({
      aliasable_type: 'Account',
      service_id: 'smartvault',
      sort: 'external_name',
      query: serviceAliasesQuery ?? undefined,
    })
      .then((res) => {
        setServiceAliasSearchResults(
          // Drop rows without an external_id: it's both the option value and the
          // index filter, so a blank one yields a dud (and duplicate) option.
          res.data
            .filter((serviceAlias) => Boolean(serviceAlias.external_id))
            .map((serviceAlias) => ({
              label: serviceAlias.external_name || serviceAlias.external_id!,
              value: serviceAlias.external_id!,
            })),
        );
      })
      .catch((error) => {
        AlertMessage(
          'error',
          errorMessageFrom(error, 'Account search failed.'),
          3000,
        );
      });
  };

  const runSync = async () => {
    if (isSyncing) return; // guard; the button is also disabled while syncing
    setIsSyncing(true);
    // Arm the completion handler BEFORE the kickoff POST: a fast backend (or a slow
    // POST response) can broadcast 'complete' before the POST promise settles.
    // Arming first lets `received()` toast + disarm correctly; arming only after the
    // await would miss that broadcast AND leave the page armed for an unrelated
    // future one (e.g. another tab's sync).
    awaitingSyncRef.current = true;
    try {
      await postApiV5FirmsAppConnectionsSmartVaultClientsSynchronization();
      // If the completion broadcast already landed during the await, `received()`
      // consumed the ref (toasted + disarmed) — don't also show the kickoff toast.
      if (awaitingSyncRef.current) {
        AlertMessage('success', SYNC_MESSAGE, 3000);
      }
    } catch (error) {
      setIsSyncing(false);
      awaitingSyncRef.current = false;
      AlertMessage(
        'error',
        errorMessageFrom(error, 'Could not start SmartVault sync.'),
        3000,
      );
    }
  };

  const paginationOffset = () => {
    if (paginationData.total_entries === 0) {
      return 0;
    }

    return (
      paginationData.current_page * paginationData.per_page -
      paginationData.per_page +
      1
    );
  };

  const paginationTo = () => paginationOffset() + serviceAliasData.length - 1;

  useEffect(() => {
    fetchServiceAliases();
  }, [showOrphans, selectedServiceAlias, page]);

  useEffect(() => {
    if (serviceAliasesQuery) fetchServiceAliasSearch();
  }, [serviceAliasesQuery]);

  useEffect(() => {
    document.title = 'SmartVault Account Links';
    fetchServiceAliasSearch();
  }, []);

  // Keep fetchRef pointed at the latest fetchServiceAliases (which closes over the
  // current page/filters) so the mount-time websocket subscription refetches with
  // the active controls, not the mount-time ones (LISD-2574). Assigned during render
  // (a ref write is render-safe and this ref is only read in the async `received`
  // handler, never during render) rather than in a useEffect — an effect runs after
  // commit, leaving a window where a `received` event could fire the stale closure.
  fetchRef.current = fetchServiceAliases;

  useEffect(() => {
    const subscription = activateActionCable();

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const handleEdit = (serviceAlias: ServiceAlias) => {
    setEditableServiceAlias(serviceAlias);
    setShowEditModal(true);
  };

  // Three confirmation states: already confirmed (check), mapped-but-unconfirmed
  // (actionable Confirm button), or unmatched (can't confirm — must link a Liscio
  // account first, per the confirmed ⟹ matched model invariant).
  const renderConfirmation = (each: ServiceAlias) => {
    if (each.confirmed) {
      // Icon only — the "Syncable" column header carries the meaning; the
      // title/aria-label keep the state readable on hover and to screen readers.
      return (
        <span
          className="text-success"
          role="img"
          title="Confirmed"
          aria-label="Confirmed"
        >
          <FontAwesomeIcon
            icon={faCircleCheck as IconProp}
            aria-hidden="true"
          />
        </span>
      );
    }

    if (each.aliasable_id) {
      // Owner-before-confirm invariant (LISD-2541): Sync Accounts must stamp
      // primary_owner before a mapping can be confirmed — the backend rejects
      // the confirm otherwise, so offer no action at all (LISD-2545), matching
      // the unmatched-alias dash below.
      if (!each.primary_owner) {
        return (
          <span
            className="text-muted"
            title="Run Sync Accounts to assign this account's SmartVault owner connection first"
          >
            -
          </span>
        );
      }

      const confirming = confirmingId === each.id;
      return (
        <button
          type="button"
          className="btn btn-sm btn-outline-success sv-row-action"
          onClick={() => handleConfirm(each)}
          disabled={confirming}
        >
          {confirming ? 'Confirming…' : 'Confirm'}
        </button>
      );
    }

    return (
      <span className="text-muted" title="Link a Liscio account first">
        -
      </span>
    );
  };

  // Live primary_owner connection-health chip (LISD-2590), shown beneath the owner
  // email. Renders only for problem states (disconnected / owner offboarded) so a
  // dead owner is visible even while sync_status still reads "Synced"; connected
  // and no_owner render nothing.
  const renderOwnerConnection = (each: ServiceAlias) => {
    const status = each.primary_owner_connection_status;
    const meta = status && OWNER_CONNECTION_META[status];
    if (!meta) return null;

    return (
      <div
        style={{ ...SYNC_CHIP_BASE, color: meta.fg, backgroundColor: meta.bg, marginTop: 4 }}
        title={meta.tip}
        aria-label={`${meta.label}: ${meta.tip}`}
      >
        <span
          aria-hidden="true"
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: meta.fg,
            flex: '0 0 auto',
          }}
        />
        {meta.label}
      </div>
    );
  };

  // Sync-health chip + the latest run's finish time (LISD-2519). The chip reflects
  // the latest sync run's outcome; the timestamp is that SAME run's finish time
  // regardless of status — so a failed / needs-reconnect row still shows when it
  // last ran (it's last_run_at, not last-successful-sync). Blank only until the
  // first run completes.
  const renderSyncStatus = (each: ServiceAlias) => {
    // Null for non-SmartVault aliases (server omits sync health for them); nothing
    // to show. Also narrows sync_status to a valid key for the lookup below.
    if (!each.sync_status) return null;

    const meta = SYNC_STATUS_META[each.sync_status] || {
      label: each.sync_status,
      fg: '#868e96',
      bg: '#f1f3f5',
    };
    // Next-step guidance for the actionable states. Exposed via title (pointer)
    // AND folded into aria-label so keyboard/screen-reader users get it too — a
    // bare title attribute isn't reliably announced.
    const guidance = SYNC_STATUS_TOOLTIP[each.sync_status];

    return (
      <span className="d-inline-flex align-items-center">
        <span
          style={{
            ...SYNC_CHIP_BASE,
            color: meta.fg,
            backgroundColor: meta.bg,
          }}
          title={guidance}
          aria-label={guidance ? `${meta.label}: ${guidance}` : undefined}
        >
          <span
            aria-hidden="true"
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: meta.fg,
              flex: '0 0 auto',
            }}
          />
          {meta.label}
        </span>
        {each.last_run_at && (
          <small
            className="text-muted ml-2"
            title={moment(each.last_run_at).format('LLL')}
          >
            {moment(each.last_run_at).fromNow()}
          </small>
        )}
      </span>
    );
  };

  // Confirming a mapped alias flips the D12 sync-eligibility gate and the
  // backend enqueues an immediate sync for it (LISD-2541; the 4h cron remains
  // the safety net). The endpoint returns the updated alias; splice it back in
  // so the row reflects "Confirmed" without a full refetch.
  const handleConfirm = (serviceAlias: ServiceAlias) => {
    const { id } = serviceAlias;
    if (!id) return;
    setConfirmingId(id);
    postApiV5FirmsServiceAliasesIdConfirm(id)
      .then((updated) => {
        // Guard the response shape AND that it's the alias we asked for before
        // splicing it into the table — otherwise a malformed/wrong-id row would
        // blank the cell (the id-match map below wouldn't replace it) while the
        // success toast still fired.
        if (!updated?.id || updated.id !== id) {
          throw new Error(
            'Confirm response was malformed or for the wrong alias.',
          );
        }
        setServiceAliasData((prev) =>
          prev.map((sa) => (sa.id === id ? updated : sa)),
        );
        AlertMessage('success', 'Account confirmed — sync starting.', 3000);
      })
      .catch((error) => {
        AlertMessage(
          'error',
          errorMessageFrom(error, 'Failed to confirm account.'),
          3000,
        );
      })
      .finally(() => {
        setConfirmingId(null);
      });
  };

  // Reset clears the owner + confirmation so a current employee can re-establish
  // the mapping (Sync Accounts re-stamps the owner, then Confirm). Admin-only and
  // destructive, so it's gated behind a confirmation modal. The endpoint returns
  // the updated (now ownerless/unconfirmed) alias; splice it back in so the row
  // reflects the new state without a full refetch (LISD-2586).
  const confirmReset = () => {
    const target = resetTarget;
    if (!target?.id) return;
    setResettingId(target.id);
    postApiV5FirmsServiceAliasesIdReset(target.id)
      .then((updated) => {
        if (!updated?.id || updated.id !== target.id) {
          throw new Error(
            'Reset response was malformed or for the wrong alias.',
          );
        }
        setServiceAliasData((prev) =>
          prev.map((sa) => (sa.id === target.id ? updated : sa)),
        );
        AlertMessage(
          'success',
          'Owner reset — run Sync Accounts, then Confirm to re-establish.',
          4000,
        );
        setResetTarget(null);
      })
      .catch((error) => {
        // Keep the modal open on failure (403 not-admin / 409 sync-in-flight) so
        // the firm can read the reason and retry or cancel.
        AlertMessage(
          'error',
          errorMessageFrom(error, 'Failed to reset owner.'),
          3000,
        );
      })
      .finally(() => {
        setResettingId(null);
      });
  };

  function handlePageChange(index: number) {
    setPage(index);
  }

  const handleSelectedServiceAliasChange = (value: AccountSearchOption | null) => {
    setPage(1);
    setSelectedServiceAlias(value);
  };

  const handleShowOrphansChange = (value: boolean) => {
    setPage(1);
    setShowOrphans(value);
  };

  return (
    <div id="content">
      {isLoading ? (
        <div
          id="loading"
          role="status"
          aria-label="Loading SmartVaultAccountLinks"
        />
      ) : null}
      <header>
        <div className="d-block">
          <ul className="breadcrumb">
            <li className="breadcrumb-item">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                onClick={() =>
                  navigate('/profile_settings?tab=third_party_apps')
                }
                className="mr-1"
              >
                <i className="icon-left-arrow label-small" aria-hidden="true" />
                THIRD PARTY APPS /
              </a>
              <span>
                Liscio <FontAwesomeIcon icon={faArrowsLeftRight as IconProp} />{' '}
                SmartVault
              </span>
            </li>
          </ul>
        </div>
      </header>
      <div className="center-wrapper">
        <div className="page-title mb-4">SmartVault Account Links</div>
        <div className="filter-wrapper d-flex">
          <div className="select-wrapper">
            <Select
              placeholder="Search SmartVault Accounts"
              className="AccountSelect"
              styles={selectStyles}
              options={serviceAliasSearchResults}
              onInputChange={debounce((e) => {
                setServiceAliasesQuery(e);
              }, 400)}
              onChange={(e) => handleSelectedServiceAliasChange(e)}
              value={selectedServiceAlias}
              isSearchable
              isClearable
            />
          </div>
          <div className="checkbox checkbox-primary check-container mt-2">
            {/* htmlFor previously pointed at a non-existent id, so the label was
                associated with nothing and the checkbox had no accessible name.
                Give the input a matching id so the association (and the
                "Show Unmatched Accounts" accessible name) actually works. */}
            <label htmlFor="show-unmatched-accounts">
              <input
                id="show-unmatched-accounts"
                type="checkbox"
                className="form-check-input"
                checked={showOrphans}
                onChange={() => handleShowOrphansChange(!showOrphans)}
              />
              <i className="checkmark" />
              <span className="text-body pl-2">Show Unmatched Accounts</span>
            </label>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <button
              type="button"
              style={{ height: '36px', padding: '0' }}
              onClick={() => runSync()}
              disabled={isSyncing}
              className="btn btn-primary px-3"
            >
              <span className="mr-2">
                <FontAwesomeIcon
                  icon={faArrowsRotate as IconProp}
                  className={isSyncing ? 'fa-spin' : ''}
                />
              </span>
              {isSyncing ? 'Syncing…' : 'Sync Accounts'}
            </button>
          </div>
        </div>

        <div className="tableWrap">
          <div className="tRow tRow--head">
            <div className="row">
              <div className="col thCol">SmartVault Account</div>
              <div className="col thCol">Liscio Account</div>
              {enableSmartVaultMirror && <div className="col thCol">Owner</div>}
              {enableSmartVaultMirror && (
                <div className="col thCol">Syncable</div>
              )}
              {enableSmartVaultMirror && (
                <div className="col thCol">Sync Status</div>
              )}
              <div className="col thCol">Date Created</div>
              <div className="col-1" />
            </div>
          </div>

          <div className="tRow tRow--body">
            {serviceAliasData.map((each) => (
              <div className="row tdBtn" key={each.id}>
                <div className="col tdCol">{each.external_name}</div>
                <div className="col tdCol">{each.aliasable?.name || '-'}</div>
                {enableSmartVaultMirror && (
                  <div className="col tdCol">
                    {each.primary_owner || '-'}
                    {renderOwnerConnection(each)}
                  </div>
                )}
                {enableSmartVaultMirror && (
                  <div className="col tdCol">{renderConfirmation(each)}</div>
                )}
                {enableSmartVaultMirror && (
                  <div className="col tdCol">{renderSyncStatus(each)}</div>
                )}
                <div className="col tdCol">
                  {moment(each.created_at).format('MM/DD/YYYY')}
                </div>
                <div className="col-1">
                  {isComplete(each) ? (
                    // Complete mappings are edit-locked server-side (re-linking
                    // splits docs) regardless of the Mirror flag, so offer Reset
                    // here too — it routes through the re-establish flow.
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-sv-reset tdBtn__hover"
                      onClick={() => setResetTarget(each)}
                      title="Reset the owner & confirmation so a current employee can re-establish this mapping"
                    >
                      Reset
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn--onlyicon btn-link tdBtn__hover px-1"
                      onClick={() => handleEdit(each)}
                      title="Edit account link"
                      aria-label="Edit account link"
                    >
                      <i className="icon-edit" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <Pagination
          currentPage={paginationData.current_page}
          totalPages={paginationData.total_pages}
          totalRecords={paginationData.total_entries}
          offset={paginationOffset()}
          off={paginationTo()}
          handlePageChange={handlePageChange}
        />
      </div>
      {showEditModal && (
        <EditLinkingModal
          serviceAlias={editableServiceAlias}
          serviceAliasData={serviceAliasData}
          setServiceAliasData={setServiceAliasData}
          closeModal={() => setShowEditModal(false)}
        />
      )}
      {resetTarget && (
        <ConfirmationModal
          isOpen
          className=""
          testid="smartvault_reset"
          headerText="Reset SmartVault owner?"
          messageText={`Clears the owner and confirmation for "${
            resetTarget.external_name || 'this mapping'
          }" and pauses syncing — the Liscio account link is kept. To resume, a current team member runs Sync Accounts to reassign the owner, then Confirm.`}
          yesBtnText={resettingId === resetTarget.id ? 'Resetting…' : 'Reset'}
          yesBtnClassName="btn btn-sv-reset"
          noBtnText="Cancel"
          disableYesBtn={resettingId === resetTarget.id}
          disableNoBtn={resettingId === resetTarget.id}
          loading={resettingId === resetTarget.id}
          yesBtnClick={confirmReset}
          // The modal's X-close also calls noBtnClick (it ignores disableNoBtn),
          // so guard it: don't dismiss while the reset POST is in flight.
          noBtnClick={() => {
            if (resettingId !== resetTarget.id) setResetTarget(null);
          }}
        />
      )}
    </div>
  );
}
